import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { SearchField } from '@/shared/ui/SearchField';
import { Tooltip } from '@/shared/ui/Tooltip';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { UserScreenNode } from '@/shared/types/system';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useTabsStore } from '../tabs/tabsStore';
import { filterTree, isLeaf, useMenu } from './useMenu';
import { NavIcon } from './NavIcon';
import { NavNode } from './NavNode';

/** Ağaçtaki ilk ekran (Enter ile açılacak sonuç). */
function firstLeaf(nodes: UserScreenNode[]): UserScreenNode | null {
  for (const n of nodes) {
    if (isLeaf(n)) return n;
    const hit = firstLeaf(n.children);
    if (hit) return hit;
  }
  return null;
}

/**
 * Daraltılmış menüde arama (#497): rayın en üstünde büyüteç; tıklanınca (ya da Ctrl+K) menüyü
 * genişletmeden yana bir arama paneli açılır. Sonuçlar açık menüdeki aramayla AYNI
 * (`filterTree`, Türkçe karakter duyarsız); ekran seçilince panel kapanır, menü daralmış kalır.
 * Arama boşken favoriler hızlı erişim için listelenir. Enter ilk sonucu açar.
 */
export function RailSearch() {
  const { t } = useTranslation();
  const { tree, favorites } = useMenu();
  const openScreen = useOpenScreen();
  const activeScreenId = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId)?.screenId ?? null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query, 80);
  const filtered = useMemo(() => filterTree(tree, q), [tree, q]);

  // Ctrl+K: menü daralmışken aramayı açar (açık menüde NavMenu kutuya odaklanır).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const setPanel = (o: boolean) => {
    setOpen(o);
    if (!o) setQuery('');
  };
  const onOpen = (n: UserScreenNode) => {
    openScreen(n);
    setPanel(false);
  };
  const label = `${t('shell.searchMenu')} (Ctrl+K)`;

  return (
    <Popover open={open} onOpenChange={setPanel}>
      <Tooltip content={label} side="right">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="hover:bg-bg-hover data-[state=open]:bg-bg-hover text-fg-muted rounded-md p-2"
          >
            <Search className="h-5 w-5" />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="flex max-h-[75vh] w-80 flex-col gap-1 p-2"
      >
        <div
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const first = q ? firstLeaf(filtered) : (favorites[0] ?? null);
            if (first) {
              e.preventDefault();
              onOpen(first);
            }
          }}
        >
          <SearchField
            autoFocus
            value={query}
            onChange={setQuery}
            placeholder={t('shell.searchMenu')}
            onEscape={() => setPanel(false)}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {q ? (
            filtered.length ? (
              filtered.map((n) => (
                <NavNode
                  key={n.screenId}
                  node={n}
                  depth={0}
                  forceOpen
                  activeScreenId={activeScreenId}
                  onOpen={onOpen}
                />
              ))
            ) : (
              <p className="text-fg-muted px-2 py-3 text-sm">{t('shell.noMenuResult')}</p>
            )
          ) : favorites.length ? (
            <>
              <div className="text-fg-subtle px-2 pt-1 pb-0.5 text-[11px] font-semibold uppercase">
                {t('shell.favorites')}
              </div>
              {favorites.map((n) => (
                <button
                  key={n.screenId}
                  type="button"
                  onClick={() => onOpen(n)}
                  className="hover:bg-bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
                >
                  <NavIcon node={n} />
                  <TruncatedText text={n.caption} className="min-w-0 flex-1" />
                </button>
              ))}
            </>
          ) : (
            <p className="text-fg-muted px-2 py-3 text-sm">{t('shell.typeToSearch')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
